/**
 * Utilidades para manejar respuestas HTTP de manera segura
 */

/**
 * Función segura para parsear respuestas JSON
 * Maneja casos donde la respuesta no es JSON válido
 */
export async function safeJsonParse(response: Response): Promise<any> {
  const responseText = await response.text();

  console.log(`🔍 HTTP Response Debug:`, {
    status: response.status,
    statusText: response.statusText,
    contentType: response.headers.get('content-type'),
    url: response.url,
    responsePreview: responseText.substring(0, 200)
  });

  try {
    // Intentar parsear como JSON primero
    return JSON.parse(responseText);
  } catch (parseError) {
    console.error('❌ ERROR JSON PARSING:', {
      status: response.status,
      statusText: response.statusText,
      responseText: responseText.substring(0, 300),
      parseError: parseError.message,
      url: response.url
    });

    // Si la respuesta no es JSON válido, crear un objeto de error
    if (response.status >= 400) {
      return {
        success: false,
        message: `Error del servidor (${response.status}): ${response.statusText}`,
        error: 'INVALID_JSON_RESPONSE',
        debug: {
          responseText: responseText.substring(0, 500),
          contentType: response.headers.get('content-type'),
          url: response.url
        }
      };
    } else {
      // Respuesta exitosa pero no JSON - esto es raro pero manejable
      return {
        success: true,
        message: 'Datos procesados (respuesta no estándar)',
        debug: responseText.substring(0, 200)
      };
    }
  }
}

/**
 * Función wrapper para fetch con manejo seguro de respuestas
 */
export async function safeFetch(url: string, options: RequestInit = {}): Promise<any> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    return await safeJsonParse(response);
  } catch (networkError) {
    console.error('❌ NETWORK ERROR:', networkError);
    return {
      success: false,
      message: 'Error de conexión de red',
      error: 'NETWORK_ERROR',
      debug: networkError.message
    };
  }
}

/**
 * Función específica para el proceso de guardado con mejor manejo de errores
 */
export async function safeSaveFetch(url: string, data: any, token?: string): Promise<any> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return await safeFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
}

/**
 * Función para detectar problemas comunes de servidor
 */
export function diagnoseServerResponse(result: any): string | null {
  // Detectar respuestas que indican problemas comunes
  if (result.error === 'INVALID_JSON_RESPONSE') {
    return 'El servidor devolvió una respuesta no válida. Esto puede indicar problemas de configuración del servidor o conflictos de versiones.';
  }

  if (result.error === 'NETWORK_ERROR') {
    return 'Problema de conexión de red. Verifique su conexión a internet.';
  }

  if (result.message && result.message.includes('timeout')) {
    return 'El servidor tardó demasiado en responder. Puede estar sobrecargado.';
  }

  if (result.message && result.message.includes('modificado por otro usuario')) {
    return 'Conflicto de versiones: otro usuario modificó los mismos datos.';
  }

  return null; // No hay problemas específicos detectados
}

/**
 * Función para crear mensajes de error más amigables para el usuario
 */
export function createUserFriendlyError(result: any): string {
  const diagnosis = diagnoseServerResponse(result);

  if (diagnosis) {
    return diagnosis;
  }

  // Mensajes genéricos pero útiles
  if (result.message && result.message.includes('Error interno del servidor')) {
    return 'Error interno del servidor. Por favor, inténtelo nuevamente o contacte al soporte técnico si el problema persiste.';
  }

  if (result.message && result.message.includes('Unauthorized')) {
    return 'Sesión expirada. Por favor, recargue la página e inicie sesión nuevamente.';
  }

  // Fallback al mensaje original o uno genérico
  return result.message || 'Error desconocido. Por favor, inténtelo nuevamente.';
}
